import {loginApi} from '@apis';
import {I18N_EN, I18N_ES_MX} from '@constants/languages';
import {Button, Checkbox, Input} from '@components';
import useAuth from '@context/useAuth';
import logo from '@images/nls-logo.png';
import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import toast from 'react-hot-toast';
import {useMutation} from 'react-query';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {Eye, EyeOff} from 'react-feather';

const Login = () => {
  const {t, i18n} = useTranslation();
  const {onLogin} = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loginPasswordVisibility, setLoginPasswordVisibility] = useState(false);

  const {state} = useLocation();
  const history = useHistory();

  const login = useMutation(params => loginApi(params));

  useEffect(() => {
    if (login?.isError) {
      if (typeof login?.error === 'string') {
        toast.error(login?.error);
      } else {
        toast.error(t('auth.loginError'));
      }
      login?.reset();
    }

    if (login?.isSuccess) {
      onLogin({token: login?.data?.key, remember});
      history.replace('/');
    }
  }, [history, login, onLogin, remember, t]);

  const handleLogin = async e => {
    e.preventDefault();
    await login.mutate({
      email,
      password,
    });
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleLogin(e);
    }
  };

  const changeLanguage = lng => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-6xl flex flex-col lg:flex-row bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Left Panel - Form */}
        <div className="flex-1 lg:w-auto next-gradient relative p-6 sm:p-8 lg:p-16 min-h-screen lg:min-h-0 flex flex-col justify-center">
          {/* Language Selector - Responsive positioning */}
          <div className="absolute top-4 left-4 right-4 flex justify-end space-x-2 z-10">
            <button
              className={`px-2 py-1 sm:px-3 sm:py-1 rounded text-xs sm:text-sm ${
                i18n.language === I18N_ES_MX
                  ? 'bg-white text-red-600'
                  : 'bg-transparent text-white border border-white'
              }`}
              onClick={() => changeLanguage(I18N_ES_MX)}>
              {t('auth.spanish')}
            </button>
            <button
              className={`px-2 py-1 sm:px-3 sm:py-1 rounded text-xs sm:text-sm ${
                i18n.language === I18N_EN
                  ? 'bg-white text-red-600'
                  : 'bg-transparent text-white border border-white'
              }`}
              onClick={() => changeLanguage(I18N_EN)}>
              {t('auth.english')}
            </button>
          </div>

          {/* Form Content */}
          <div className="w-full max-w-md mx-auto mt-12 lg:mt-0">
            <div className="text-center mb-8">
              <h1 className="font-bold text-2xl sm:text-3xl text-white">
                {t('auth.welcome')}
              </h1>
              <p className="font-light text-xs sm:text-sm text-white mt-3">
                {t('auth.loginToAccount')}
              </p>
            </div>

            <div className="space-y-4">
              <Input
                dark
                white
                value={email}
                onChange={e => setEmail(e.target.value)}
                label={t('auth.email')}
              />
              <div className="position-rel">
                <Input
                  dark
                  white
                  type={loginPasswordVisibility ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  label={t('auth.password')}
                  onKeyDown={handleKeyDown}
                />
                <div
                  className="password-toggle"
                  onClick={() => {
                    setLoginPasswordVisibility(!loginPasswordVisibility);
                  }}>
                  {loginPasswordVisibility ? (
                    <Eye size="14" color="black" />
                  ) : (
                    <EyeOff size="14" color="black" />
                  )}
                </div>
              </div>

              <div className="flex flex-row items-center justify-between">
                <Checkbox
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                  label={t('auth.rememberMe')}
                />
                <Link
                  to="/forgot-password"
                  className="text-white text-xs underline">
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </div>

            <div className="mt-8 space-y-4">
              <Button
                block
                loading={login?.isLoading}
                disabled={login?.isLoading || !email || !password}
                onClick={handleLogin}
                label={t('auth.signIn')}
              />
              <div className="text-center">
                <p className="text-white text-xs">
                  {t('auth.dontHaveAccount')}
                </p>
                <Link
                  to="/register"
                  className="text-white text-xs underline ml-1">
                  {t('auth.signUpNow')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Logo (hidden on small screens, visible on lg+) */}
        <div className="hidden lg:flex lg:w-96 xl:w-[400px] justify-center items-center next-gradient p-8">
          <img
            className="object-contain max-w-full max-h-80"
            src={logo}
            alt="Next Level Technology Logo"
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
